import React, { useState, forwardRef, useEffect, useMemo } from 'react';

import TableContainer from '@hud/web-components/Container/TableContainer';
import { DefaultTable } from '@hud/web-components/Table/Table';
import { Search } from '@hud/web-components/Search/Search';
import { ItemCounter } from '@hud/web-components/ItemCounter/ItemCounter';
import {
  Endpoint,
  StatusCodeBreakdown,
} from '@hud/portal-dtos/webapp-endpoint.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import {
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import metricProgressCellRenderer from '../../../components/metricProgressCellRenderer/metricProgressCellRenderer';
import { DEFAULT_PAGE_SIZE } from '../../../consts';
import useAnalytics from '../../../hooks/useAnalytics';
import renderMetric, {
  formatMetric,
} from '../../../components/metricCellRenderer/metricCellRenderer';
import useEnvironment from '../../../hooks/useEnvironment';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import httpStatusCodeToString from './httpStatusCodeToString';

export interface EndpointStatusCodesTableProps {
  statusCodes?: StatusCodeBreakdown;
  endpoint?: Endpoint;
  loading: boolean;
}

const columnHelper = createColumnHelper<StatusCodeBreakdown[0]>();

const EndpointStatusCodesTable = forwardRef<
  HTMLDivElement,
  EndpointStatusCodesTableProps
>(({ statusCodes, endpoint, loading }, ref) => {
  const analytics = useAnalytics();
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'normalizedCount.value',
      desc: true,
    },
  ]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const { environment } = useEnvironment();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('name', {
        header: 'Status Code',
        cell: (cell) => (
          <LongContentPopover>
            {cell.getValue()} {httpStatusCodeToString(cell.getValue())}
          </LongContentPopover>
        ),
        meta: {
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('metrics.count.value', {
        id: 'count.value',
        header: 'Count',
        cell: renderMetric('number'),
        size: 100,
        sortingFn: (a, b) =>
          a.original.metrics.count.value - b.original.metrics.count.value,
        meta: {
          fitFormatter: formatMetric('number'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.normalizedCount.value', {
        id: 'normalizedCount.value',
        header: 'Count/d',
        cell: renderMetric('number'),
        size: 100,
        sortingFn: (a, b) =>
          a.original.metrics.normalizedCount.value -
          b.original.metrics.normalizedCount.value,
        meta: {
          fitFormatter: formatMetric('number'),
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.distribution.value', {
        id: 'distribution.value',
        header: 'Distribution',
        cell: metricProgressCellRenderer,
        size: 100,
      }),
    ];
  }, []);

  const table = useReactTable({
    data: statusCodes ?? [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    columnResizeMode: 'onChange',
    enableSortingRemoval: false,
    state: {
      pagination,
      sorting,
      globalFilter,
    },
    defaultColumn: {
      minSize: 80,
    },
    onSortingChange: setSorting,
  });

  useEffect(() => {
    if (table.getState().sorting.length === 0 || !endpoint) return;
    analytics.capture('endpoint_status_codes_sorted', {
      endpoint_id: endpoint.id,
      endpoint_path: endpoint.path,
      column_name: sorting[0]?.id ?? '',
      column_order: sorting[0]?.desc ? 'desc' : 'asc',
      environment: environment?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  let subsetCounter = <Skeleton width={100} />;
  if (statusCodes) {
    subsetCounter = (
      <ItemCounter
        total={statusCodes.length}
        filtered={table.getPrePaginationRowModel().rows.length}
      />
    );
  }

  return (
    <TableContainer.Container
      ref={ref}
      title={`Status Codes`}
      id="endpoint-status-codes"
    >
      <TableContainer.Header>
        <Search
          inline
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
        {subsetCounter}
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Status Codes Table"
          loading={loading}
          loadingRows={5}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={loading ? 0 : table.getPageCount()}
          currentPage={
            loading || table.getPageCount() === 0
              ? 0
              : table.getState().pagination.pageIndex + 1
          }
          onPageChange={(page) => table.setPageIndex(page - 1)}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
});

export default EndpointStatusCodesTable;
